import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, Action } from '@ngrx/store';
import { of, Observable } from 'rxjs';
import { map, switchMap, catchError, withLatestFrom, tap, take, filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { CommunicationsApiService } from 'src/app/modules/management/store/communication-center/communications-api.service';
import { CommunicationsApiService as MassCommunicationsApiService } from 'src/app/modules/communications/store/communications/communications-api.service';
import * as sharingActions from './sharing.actions';
import { SystemNotificationsService } from '@app/core/system-notifications/system-notifications.service';

@Injectable()
export class SharingEffects {
  constructor(
    private actions$: Actions,
    private communicationsApiService: CommunicationsApiService,
    private massCommunicationsApiService: MassCommunicationsApiService,
    private notify: SystemNotificationsService,
    private store$: Store<any>,
    private translateService: TranslateService
  ) {}

  //
  // Communication Templates
  //
  getConfiguredChannelsFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getConfiguredChannelsFromApi),
      switchMap(action => {
        return this.communicationsApiService.getConfiguredChannels(action.queryParams).pipe(
          map(results => {
            return sharingActions.getConfiguredChannelsFromApiComplete({ results });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  getCommunicationTemplatesFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getCommunicationTemplatesFromApi),
      switchMap(action => {
        return this.communicationsApiService.getTemplates(action.queryParams).pipe(
          map(results => {
            return sharingActions.getCommunicationTemplatesFromApiComplete({ results });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  getCommunicationTemplatePreviewFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getCommunicationTemplatePreviewFromApi),
      switchMap(action => {
        return this.communicationsApiService.previewTemplate(action.communication).pipe(
          map(result => {
            return sharingActions.getCommunicationTemplatePreviewFromApiComplete({ renderedTemplate: result });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  sendCommunicationWithApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.sendCommunicationWithApi),
      switchMap(action => {
        return this.communicationsApiService.sendCommunication(action.communication).pipe(
          map(result => {
            const message = this.translateService.instant('MAP.COMMUNICATION-SENT');
            this.notify.success(message);
            return sharingActions.sendCommunicationWithApiComplete();
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  getCommunicationActivityForDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getCommunicationActivityForDocument),
      switchMap(action => {
        return this.communicationsApiService.getCommunicationActivityForDocument(action.documentUId).pipe(
          map(results => {
            return sharingActions.getCommunicationActivityForDocumentComplete({ data: results });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  getHistoricCommunicationFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getHistoricCommunicationFromApi),
      switchMap(action => {
        return this.communicationsApiService.getCommunication(action.communicationUId).pipe(
          map(result => {
            return sharingActions.getHistoricCommunicationFromApiComplete({
              communication: result,
              channelUId: action.channelUId,
              channelName: action.channelName
            });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  getCommunicationRecipientActivityFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getCommunicationRecipientActivityFromApi),
      switchMap(action => {
        return this.communicationsApiService.getCommunicationRecipientActivity(action.communicationUId).pipe(
          map(results => {
            return sharingActions.getCommunicationRecipientActivityFromApiComplete({
              results
            });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  getEverbridgeTemplatesForConfiguredChannelFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.getEverbridgeTemplatesForConfiguredChannelFromApi),
      switchMap(action => {
        const { channel } = action;

        return this.communicationsApiService.getEverbridgeTemplatesForConfiguredChannel(channel).pipe(
          map(templates => {
            return sharingActions.getEverbridgeTemplatesForConfiguredChannelFromApiComplete({ templates });
          }),
          catchError(err => of(sharingActions.setError({ err })))
        );
      })
    )
  );

  replaceEverbridgeTokensFromApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sharingActions.replaceEverbridgeTokensFromApi),
      switchMap(action => {
        const { template, document } = action;

        return this.communicationsApiService.replaceEverbridgeTokens(template, document).pipe(
          map(template => {
            return sharingActions.replaceEverbridgeTokensFromApiComplete({ template });
          }),
          catchError(err => of(sharingActions.setError({ err }), sharingActions.replaceEverbridgeTokensFromApiComplete({ template: null })))
        );
      })
    )
  );
}
